import { createContext, useContext, useState, useEffect, useCallback, useRef } from "react";
import axios from "axios";
import { toast } from "sonner";
import { useAuth } from "./AuthContext";

const AutoSyncContext = createContext(null);

export const useAutoSync = () => {
  const context = useContext(AutoSyncContext);
  if (!context) {
    throw new Error("useAutoSync must be used within AutoSyncProvider");
  }
  return context;
};

export const AutoSyncProvider = ({ children }) => {
  const { user, getAuthHeader } = useAuth();
  const [autoSyncEnabled, setAutoSyncEnabled] = useState(() => {
    // استرجاع الحالة من localStorage
    const saved = localStorage.getItem("autoSyncEnabled");
    return saved === "true";
  });
  const [autoSyncInterval, setAutoSyncInterval] = useState(() => {
    const saved = localStorage.getItem("autoSyncInterval");
    return saved ? Number(saved) : 60;
  });
  const [lastSyncTime, setLastSyncTime] = useState(null);
  const [syncing, setSyncing] = useState(false);
  const [syncCount, setSyncCount] = useState(0);
  const intervalRef = useRef(null);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api`;

  // حفظ الإعدادات في localStorage
  useEffect(() => {
    localStorage.setItem("autoSyncEnabled", autoSyncEnabled.toString());
  }, [autoSyncEnabled]);

  useEffect(() => {
    localStorage.setItem("autoSyncInterval", autoSyncInterval.toString());
  }, [autoSyncInterval]);

  // دالة المزامنة
  const syncLeads = useCallback(async (silent = false) => {
    if (!user || user.role !== "admin") return;
    
    setSyncing(true);
    try {
      const response = await axios.post(`${API}/meta/sync-leads`, {}, {
        headers: getAuthHeader()
      });
      
      setLastSyncTime(new Date());
      setSyncCount(prev => prev + 1);
      
      if (!silent && response.data.synced > 0) {
        toast.success(`تم مزامنة ${response.data.synced} عميل جديد`);
      }
      
      return response.data;
    } catch (error) {
      console.error("Auto sync error:", error);
      if (!silent) {
        // Don't show error for silent syncs
      }
    } finally {
      setSyncing(false);
    }
  }, [user, API, getAuthHeader]);

  // تشغيل المزامنة التلقائية
  useEffect(() => {
    // تنظيف أي interval سابق
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }

    if (autoSyncEnabled && user?.role === "admin") {
      // مزامنة أولية
      syncLeads(true);
      
      // تشغيل interval
      intervalRef.current = setInterval(() => {
        syncLeads(true);
      }, autoSyncInterval * 1000);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, [autoSyncEnabled, autoSyncInterval, user?.role, syncLeads]);

  const toggleAutoSync = useCallback((enabled) => {
    setAutoSyncEnabled(enabled);
    if (enabled) {
      toast.success("تم تفعيل المزامنة التلقائية");
    } else {
      toast.info("تم إيقاف المزامنة التلقائية");
    }
  }, []);

  const updateInterval = useCallback((seconds) => {
    setAutoSyncInterval(seconds);
  }, []);

  const value = {
    autoSyncEnabled,
    autoSyncInterval,
    lastSyncTime,
    syncing,
    syncCount,
    toggleAutoSync,
    updateInterval,
    syncLeads,
  };

  return (
    <AutoSyncContext.Provider value={value}>
      {children}
    </AutoSyncContext.Provider>
  );
};

export default AutoSyncContext;
